import { formatPrice } from '@general/helpers';
import { addItemToCart } from '@services/cart';
import { getProduct } from '@services/product';

/**
 * Event type for before variant change.
 * @constant {string}
 */
export const beforeVariantChangeType = 'beforeVariantChange';

/**
 * Event type for after variant change.
 * @constant {string}
 */
export const afterVariantChangeType = 'afterVariantChange';

/**
 * Retrieves a product variant based on the selected options in the form.
 *
 * @param {Object} params - Parameters object.
 * @param {HTMLFormElement} params.form - The form element containing product options.
 * @param {string} params.productOptions - CSS selector for the product option inputs.
 * @param {number} params.expectedOptionsLength - Expected number of product options.
 * @param {Array<Object>} params.variants - List of product variants.
 *
 * @returns {Object|undefined} The selected variant object or undefined if not found.
 */
export const getVariantFromOptions = ({
  form,
  productOptions,
  expectedOptionsLength,
  variants,
}) => {
  if (!form || !variants || !productOptions) return;
  const options = [...form.querySelectorAll(productOptions)].map((input) => ({
    name: input.name,
    value: input.value,
  }));
  if (options.length !== expectedOptionsLength) {
    console.error(
      `Unexpected number of product options. Expected value: ${expectedOptionsLength} Provided value: ${options.length}`,
    );
    return;
  }
  const variant = variants.find((variant) =>
    options.every((option) => variant[option.name] === option.value),
  );
  if (!variant) {
    console.error(
      `Cannot find variant with the selected options: ${JSON.stringify(options)}`,
    );
    return;
  }
  return variant;
};

/**
 * Updates the product price display in the specified wrapper element.
 *
 * @param {Object} params - Parameters object.
 * @param {HTMLElement} params.wrapper - The element containing price elements to update.
 * @param {string} [params.compareAtPriceSelector='[data-compare-at-price]'] - CSS selector for the compare-at price elements.
 * @param {string} [params.priceSelector='[data-price]'] - CSS selector for the price elements.
 * @param {number} [params.compareAtPrice] - The compare-at price to display.
 * @param {number} [params.price] - The current price to display.
 * @param {Object} [params.variant] - The product variant object containing price information.
 */
export const updateProductPrice = ({
  wrapper,
  compareAtPriceSelector = '[data-compare-at-price]',
  priceSelector = '[data-price]',
  compareAtPrice,
  price,
  variant,
}) => {
  if (!wrapper || !variant || variant == null) return;
  const compareAtPriceContent = formatPrice({
    value: compareAtPrice ?? variant?.compare_at_price,
  });
  wrapper
    .querySelectorAll(compareAtPriceSelector)
    .forEach((compareAtPriceWrapper) => {
      compareAtPriceWrapper.innerText = compareAtPriceContent ?? '';
      compareAtPriceWrapper.style.display = compareAtPriceContent ? '' : 'none';
    });
  const priceContent = formatPrice({
    value: price ?? variant?.price,
  });
  wrapper.querySelectorAll(priceSelector).forEach((priceWrapper) => {
    priceWrapper.innerText = priceContent ?? '';
    priceWrapper.style.display = priceContent ? '' : 'none';
  });
};

/**
 * Initializes the product form, sets up event listeners, and handles product variant changes.
 *
 * @param {Object} params - Parameters object.
 * @param {HTMLFormElement} params.form - The form element to initialize.
 * @param {Object} [params.selectors] - Object containing CSS selectors for form elements.
 * @param {string} [params.selectors.id='[name="id"]'] - CSS selector for the ID field.
 * @param {string} [params.selectors.submit='[type="submit"]'] - CSS selector for the submit element.
 * @param {string} [params.selectors.productOptions='input[name*="option"]:checked'] - CSS selector for the product options.
 * @param {string} [params.selectors.compareAtPrice='[data-compare-at-price]'] - CSS selector for the compare-at price elements.
 * @param {string} [params.selectors.price='[data-price]'] - CSS selector for the price elements.
 * @param {Object} [params.product] - Object containing product information and selectors.
 * @param {string} [params.product.jsonSelector='[data-product-json]'] - CSS selector for the product JSON script tag.
 * @param {string} [params.product.url] - URL to fetch product data if not available in the JSON.
 * @param {string} [params.product.handle] - Handle to fetch product data if not available in the JSON.
 * @param {Object} [params.callbacks] - Object containing callback functions for form events.
 * @param {Function} [params.callbacks.beforeVariantChange] - Function to call before variant change.
 * @param {Function} [params.callbacks.afterVariantChange] - Function to call after variant change.
 * @param {Function} [params.callbacks.beforeSubmit] - Function to call before form submit.
 * @param {Function} [params.callbacks.afterSubmit] - Function to call after form submit.
 * @param {Function} [params.callbacks.errorCallback] - Function to call if the form submits enounters an error.
 */
export const initProductForm = async ({
  form,
  selectors: {
    id = '[name="id"]',
    submit = '[type="submit"]',
    productOptions = 'input[name*="option"]:checked',
    compareAtPrice = '[data-compare-at-price]',
    price = '[data-price]',
  } = {
    id: '[name="id"]',
    productOptions: 'input[name*="option"]:checked',
    compareAtPrice: '[data-compare-at-price]',
    price: '[data-price]',
  },
  product: { jsonSelector = '[data-product-json]', url, handle } = {
    jsonSelector: '[data-product-json]',
  },
  callbacks: {
    beforeVariantChange,
    afterVariantChange,
    beforeSubmit,
    afterSubmit,
    errorCallback,
  } = {},
}) => {
  if (!form) {
    console.error('Unable to initialize form, missing form element.');
    return;
  }
  const idField = form.querySelector(id);
  const submitEl = form.querySelector(submit);
  const jsonScript = form.querySelector(jsonSelector);
  const productJson = jsonScript
    ? JSON.parse(jsonScript.innerText)
    : await getProduct({ url, handle });
  if (!productJson) {
    console.error(
      `Cannot find product Object. script selector: ${jsonSelector}, product selectors: ${JSON.stringify({ url, handle })}`,
    );
    return;
  }
  if (!idField) {
    console.error(`Missing ID field, selector used: ${id}`);
    return;
  }

  var selectedVariant = getVariantFromOptions({
    form,
    productOptions,
    expectedOptionsLength: productJson.options.length,
    variants: productJson.variants,
  });
  if (submitEl && selectedVariant != undefined)
    submitEl.disabled =
      typeof selectedVariant.available !== 'undefined' &&
      !selectedVariant.available;

  const events = {
    [`${beforeVariantChangeType}`]: new CustomEvent(beforeVariantChangeType),
    [`${afterVariantChangeType}`]: new CustomEvent(afterVariantChangeType),
  };
  Object.keys(events).forEach((eventType) => {
    events[eventType].variant = selectedVariant;
    events[eventType].product = productJson;
  });

  form.addEventListener('change', async () => {
    form.dispatchEvent(events[beforeVariantChangeType]);
    if (typeof beforeVariantChange === 'function')
      await beforeVariantChange({
        variant: selectedVariant,
        product: productJson,
      });
    selectedVariant = getVariantFromOptions({
      form,
      productOptions,
      expectedOptionsLength: productJson.options.length,
      variants: productJson.variants,
    });
    updateProductPrice({
      wrapper: form,
      compareAtPriceSelector: compareAtPrice,
      priceSelector: price,
      variant: selectedVariant,
    });
    Object.keys(events).forEach(
      (eventType) => (events[eventType].variant = selectedVariant),
    );
    idField.value = selectedVariant.id;
    if (submitEl)
      submitEl.disabled =
        typeof selectedVariant.available !== 'undefined' &&
        !selectedVariant.available;
    form.dispatchEvent(events[afterVariantChangeType]);
    if (typeof afterVariantChange === 'function')
      await afterVariantChange({
        variant: selectedVariant,
        product: productJson,
      });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    await addItemToCart({
      form,
      callbacks: {
        beforeFetch: beforeSubmit,
        afterFetch: afterSubmit,
        errorCallback,
      },
    });
  });
};
